import { useState, useCallback, useEffect } from 'react';

interface ColumnWidths {
  [key: string]: number;
}

interface UseColumnResizeOptions {
  defaultWidths: ColumnWidths;
  minWidth?: number;
}

interface UseColumnResizeReturn {
  columnWidths: ColumnWidths;
  handleMouseDown: (e: React.MouseEvent, column: string) => void;
  isResizing: boolean;
}

export const useColumnResize = ({
  defaultWidths,
  minWidth = 80,
}: UseColumnResizeOptions): UseColumnResizeReturn => {
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(defaultWidths);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, column: string) => {
      e.preventDefault();
      e.stopPropagation();
      setResizingColumn(column);
      setStartX(e.clientX);
      setStartWidth(columnWidths[column] || defaultWidths[column] || minWidth);
    },
    [columnWidths, defaultWidths, minWidth],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!resizingColumn) return;

      const diff = e.clientX - startX;
      const newWidth = Math.max(minWidth, startWidth + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: newWidth,
      }));
    },
    [resizingColumn, startX, startWidth, minWidth],
  );

  const handleMouseUp = useCallback(() => {
    setResizingColumn(null);
  }, []);

  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [resizingColumn, handleMouseMove, handleMouseUp]);

  return {
    columnWidths,
    handleMouseDown,
    isResizing: resizingColumn !== null,
  };
};
